import { KanbanTask, BoardConfig, GroupedTasks, DataScope } from '@/types';

/**
 * 📂 UNIVERSAL GROUPING ENGINE v2.0
 * Refactored for Hybrid Scoping: Partitions data based on privacy boundaries 
 * before applying context-based grouping.
 */
export const groupTasksByContext = (
  tasks: KanbanTask[], 
  config: BoardConfig,
  filterValue: string = 'ALL',
  // 🛡️ Hybrid Scoping Params
  currentUserId: string,
  currentHouseholdId: string | null,
  activeScope: DataScope = 'BOTH'
): GroupedTasks => {
  
  // 1. Initial Filter (Privacy Boundaries & Board Meta)
  const filteredTasks = (tasks || []).filter((task: KanbanTask) => {
    // 🛡️ SCOPE FIREWALL: Verify data origin matches requested toggle
    const isPersonal = task.scope === 'personal' || !task.scope;
    const isShared = task.scope === 'household';

    // Logic: If no household active, force Personal. If active, respect toggle.
    let scopeMatches = true;
    if (!currentHouseholdId) {
      scopeMatches = isPersonal;
    } else {
      if (activeScope === 'PERSONAL') scopeMatches = isPersonal;
      if (activeScope === 'SHARED') scopeMatches = isShared;
    }

    // 🔒 PRIVACY GUARD: Never leak personal tasks not owned by the requester
    const ownerCheck = isPersonal ? task.ownerId === currentUserId : true;

    const isCorrectBoard = task.boardId === config.id;
    const isNotDeleted = !task.isDeleted;
    
    const tags = task.tags || [];
    const priority = task.priorityTier || 'none';
    const unit = task.consumption?.unit || '';

    const matchesFilter = filterValue === 'ALL' || 
      tags.includes(filterValue) || 
      priority === filterValue ||
      unit === filterValue;

    return isCorrectBoard && isNotDeleted && matchesFilter && scopeMatches && ownerCheck;
  });

  // 2. Perform Grouping based on Board Configuration
  return filteredTasks.reduce((groups: GroupedTasks, task: KanbanTask) => {
    let groupKey = 'Uncategorized';

    // 🛰️ NEW: Hybrid Scope Grouping
    // If user wants to see 'BOTH', we can optionally prefix or group by scope
    if (config.defaultGroupBy as any === 'scope') {
      groupKey = task.scope === 'personal' ? 'Personal Partition' : 'Household Hub';
    } else {
      switch (config.defaultGroupBy) {
        case 'category':
          groupKey = (task.tags && task.tags.length > 0) ? task.tags[0] : 'General';
          break;
        
        case 'priority':
          groupKey = (task.priorityTier && task.priorityTier !== 'none') 
            ? task.priorityTier 
            : 'No Priority';
          break;

        case 'status':
          groupKey = task.status || 'todo';
          break;

        case 'none':
        default:
          groupKey = 'Items';
          break;
      }
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    
    groups[groupKey].push(task);
    return groups;
  }, {} as GroupedTasks);
};

/**
 * 📊 SORTING UTILITY
 * Logic for ensuring groups appear in a meaningful hierarchy.
 */
export const sortGroupKeys = (
  keys: string[], 
  groupBy: 'category' | 'priority' | 'status' | 'none' | 'scope'
): string[] => {
  const sorted = [...keys];

  if (groupBy === 'priority') {
    const order = ['P1', 'P2', 'P3', 'No Priority'];
    return sorted.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  if (groupBy === 'status') {
    const order = ['todo', 'in_progress', 'pantry', 'finalized', 'backlog', 'history'];
    return sorted.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  if (groupBy === 'scope') {
    const order = ['Personal Partition', 'Household Hub'];
    return sorted.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  return sorted.sort((a, b) => a.localeCompare(b));
};